import { useState, useEffect, useCallback } from 'react';
import { rmmAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, Search, ChevronLeft, ChevronRight, ArrowUpDown } from 'lucide-react';
import { DeviceDetail } from './DeviceDetail';
import { fmtWhen, pctColor } from './rmmUtils';

export const DevicesTab = ({ isAdmin }) => {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sort, setSort] = useState('hostname');
  const [order, setOrder] = useState('asc');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    rmmAPI.listDevices({
      search: search || undefined,
      status: statusFilter === 'all' ? undefined : statusFilter,
      sort, order, page, page_size: 25,
    }).then((res) => setData(res.data)).catch(() => setData({ devices: [], total: 0 }));
  }, [search, statusFilter, sort, order, page]);

  useEffect(() => {
    const t = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const toggleSort = (field) => {
    if (sort === field) setOrder(order === 'asc' ? 'desc' : 'asc');
    else { setSort(field); setOrder('asc'); }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / 25)) : 1;

  const SortHead = ({ field, children }) => (
    <TableHead className="cursor-pointer select-none" onClick={() => toggleSort(field)}>
      <span className="inline-flex items-center gap-1">{children}<ArrowUpDown className="w-3 h-3 text-slate-400" /></span>
    </TableHead>
  );

  return (
    <div className="space-y-4" data-testid="rmm-devices-tab">
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-56">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search hostname, customer, user, IP..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-9 bg-white border-slate-200"
            data-testid="rmm-device-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-36 bg-white border-slate-200" data-testid="rmm-device-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="online">Online</SelectItem>
            <SelectItem value="offline">Offline</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="bg-white border-slate-200">
        <CardContent className="p-0">
          {!data ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>
          ) : (
            <Table data-testid="rmm-devices-table">
              <TableHeader>
                <TableRow>
                  <SortHead field="hostname">Hostname</SortHead>
                  <SortHead field="customer_name">Customer</SortHead>
                  <SortHead field="status">Status</SortHead>
                  <SortHead field="cpu_percent">CPU</SortHead>
                  <SortHead field="ram_percent">RAM</SortHead>
                  <SortHead field="disk_percent">Disk</SortHead>
                  <TableHead>User</TableHead>
                  <TableHead>IP</TableHead>
                  <SortHead field="last_seen">Last Seen</SortHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.devices.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-8">No devices enrolled yet. Create an enrollment key in the Enrollment tab, then run the agent or simulator.</TableCell></TableRow>
                )}
                {data.devices.map((d) => (
                  <TableRow key={d.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelected(d.id)} data-testid={`rmm-device-row-${d.id}`}>
                    <TableCell className="font-medium text-slate-800">{d.hostname}</TableCell>
                    <TableCell className="text-slate-600">{d.customer_name || '—'}{d.site_name ? ` · ${d.site_name}` : ''}</TableCell>
                    <TableCell>
                      {d.token_revoked
                        ? <Badge className="bg-slate-200 text-slate-600">revoked</Badge>
                        : d.status === 'online'
                          ? <Badge className="bg-emerald-100 text-emerald-700">online</Badge>
                          : <Badge className="bg-red-100 text-red-700">offline</Badge>}
                    </TableCell>
                    <TableCell className={pctColor(d.cpu_percent)}>{d.cpu_percent != null ? `${Math.round(d.cpu_percent)}%` : '—'}</TableCell>
                    <TableCell className={pctColor(d.ram_percent)}>{d.ram_percent != null ? `${Math.round(d.ram_percent)}%` : '—'}</TableCell>
                    <TableCell className={pctColor(d.disk_percent)}>{d.disk_percent != null ? `${Math.round(d.disk_percent)}%` : '—'}</TableCell>
                    <TableCell className="text-slate-600 text-xs">{d.logged_in_user || '—'}</TableCell>
                    <TableCell className="text-slate-600 text-xs font-mono">{d.ip_address || '—'}</TableCell>
                    <TableCell className="text-slate-500 text-xs">{fmtWhen(d.last_seen)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {data && data.total > 25 && (
        <div className="flex items-center justify-end gap-2 text-sm text-slate-500">
          Page {page} of {totalPages} ({data.total} devices)
          <Button size="icon" variant="outline" className="rounded-full h-8 w-8" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="rmm-devices-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button size="icon" variant="outline" className="rounded-full h-8 w-8" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="rmm-devices-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      )}

      {selected && (
        <DeviceDetail deviceId={selected} isAdmin={isAdmin} onClose={() => setSelected(null)} onChanged={load} />
      )}
    </div>
  );
};
